"use client";

import { useState, useCallback } from "react";
import {
  Plus,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import type { DataGridField, DataGridColumn, DropdownOption } from "./types";

// =============================================================================
// DATA GRID FIELD RENDERER - Editable table with inline editing
// =============================================================================

interface DataGridFieldRendererProps {
  field: DataGridField;
  value: Record<string, unknown>[] | undefined;
  onChange: (value: Record<string, unknown>[]) => void;
  error?: string;
  disabled?: boolean;
}

export function DataGridFieldRenderer({
  field,
  value = [],
  onChange,
  error,
  disabled,
}: DataGridFieldRendererProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const rows = value || [];
  const canAddRow = !field.max_rows || rows.length < field.max_rows;
  const canDeleteRow =
    field.allow_delete !== false && rows.length > (field.min_rows || 0);

  // Add new row
  const handleAddRow = useCallback(() => {
    if (!canAddRow || disabled) return;

    const newRow: Record<string, unknown> = {};
    field.columns.forEach((col) => {
      newRow[col.key] = col.type === "boolean" ? false : "";
    });

    onChange([...rows, newRow]);
  }, [canAddRow, disabled, field.columns, rows, onChange]);

  // Remove row
  const handleRemoveRow = useCallback(
    (index: number) => {
      if (!canDeleteRow || disabled) return;
      onChange(rows.filter((_, i) => i !== index));
    },
    [canDeleteRow, disabled, rows, onChange]
  );

  // Update cell value
  const handleCellChange = useCallback(
    (rowIndex: number, column: string, cellValue: unknown) => {
      const newRows = [...rows];
      newRows[rowIndex] = { ...newRows[rowIndex], [column]: cellValue };
      onChange(newRows);
    },
    [rows, onChange]
  );

  // Move row up
  const handleMoveUp = useCallback(
    (index: number) => {
      if (index === 0 || disabled) return;
      const newRows = [...rows];
      [newRows[index - 1], newRows[index]] = [newRows[index], newRows[index - 1]];
      onChange(newRows);
    },
    [rows, disabled, onChange]
  );

  // Move row down
  const handleMoveDown = useCallback(
    (index: number) => {
      if (index === rows.length - 1 || disabled) return;
      const newRows = [...rows];
      [newRows[index], newRows[index + 1]] = [newRows[index + 1], newRows[index]];
      onChange(newRows);
    },
    [rows, disabled, onChange]
  );

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    if (!field.allow_reorder || disabled) return;
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      return;
    }

    const newRows = [...rows];
    const [draggedRow] = newRows.splice(draggedIndex, 1);
    newRows.splice(index, 0, draggedRow);
    onChange(newRows);
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">
          {field.display_name}
          {field.required && <span className="text-red-400 ml-1">*</span>}
        </label>
        {!disabled && canAddRow && (
          <button
            type="button"
            onClick={handleAddRow}
            className="flex items-center gap-1 rounded bg-secondary px-2 py-1 text-xs text-foreground hover:bg-secondary"
          >
            <Plus className="h-3 w-3" />
            {field.add_row_label || "Add Row"}
          </button>
        )}
      </div>

      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}

      {/* Grid */}
      <div className="overflow-auto rounded border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              {/* Reorder column */}
              {field.allow_reorder && (
                <th className="w-8 border-b border-border p-2"></th>
              )}

              {/* Data columns */}
              {field.columns.map((column) => (
                <th
                  key={column.key}
                  className="border-b border-border p-2 text-left font-medium text-foreground"
                  style={{ width: column.width }}
                >
                  {column.label}
                  {column.required && (
                    <span className="text-red-400 ml-0.5">*</span>
                  )}
                </th>
              ))}

              {/* Actions column */}
              {(canDeleteRow || field.allow_reorder) && (
                <th className="w-20 border-b border-border p-2"></th>
              )}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    field.columns.length +
                    (field.allow_reorder ? 1 : 0) +
                    (canDeleteRow || field.allow_reorder ? 1 : 0)
                  }
                  className="p-8 text-center text-muted-foreground"
                >
                  {field.empty_message || "No rows added yet"}
                </td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  draggable={field.allow_reorder && !disabled}
                  onDragStart={() => handleDragStart(rowIndex)}
                  onDragOver={(e) => handleDragOver(e, rowIndex)}
                  onDrop={() => handleDrop(rowIndex)}
                  className={`border-b border-border/50 ${
                    draggedIndex === rowIndex ? "opacity-50" : ""
                  }`}
                >
                  {/* Drag handle */}
                  {field.allow_reorder && (
                    <td className="p-2 text-center">
                      <GripVertical
                        className={`h-4 w-4 ${
                          disabled
                            ? "text-muted-foreground"
                            : "text-muted-foreground cursor-grab"
                        }`}
                      />
                    </td>
                  )}

                  {/* Data cells */}
                  {field.columns.map((column) => (
                    <td key={column.key} className="p-1">
                      <CellEditor
                        column={column}
                        value={row[column.key]}
                        onChange={(val) =>
                          handleCellChange(rowIndex, column.key, val)
                        }
                        disabled={disabled}
                        rowData={row}
                      />
                    </td>
                  ))}

                  {/* Actions */}
                  {(canDeleteRow || field.allow_reorder) && (
                    <td className="p-2">
                      <div className="flex items-center justify-end gap-1">
                        {field.allow_reorder && !disabled && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleMoveUp(rowIndex)}
                              disabled={rowIndex === 0}
                              className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30"
                              title="Move up"
                            >
                              <ChevronUp className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveDown(rowIndex)}
                              disabled={rowIndex === rows.length - 1}
                              className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30"
                              title="Move down"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </>
                        )}
                        {canDeleteRow && !disabled && (
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(rowIndex)}
                            className="rounded p-1 text-muted-foreground hover:bg-red-900/30 hover:text-red-400"
                            title="Remove row"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Row count info */}
      {(field.min_rows || field.max_rows) && (
        <p className="text-xs text-muted-foreground">
          {field.min_rows && field.max_rows
            ? `${field.min_rows} - ${field.max_rows} rows allowed`
            : field.min_rows
            ? `Minimum ${field.min_rows} rows`
            : `Maximum ${field.max_rows} rows`}
          {" "}({rows.length} current)
        </p>
      )}

      {/* Error */}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

// =============================================================================
// CELL EDITOR - Renders appropriate input based on column type
// =============================================================================

interface CellEditorProps {
  column: DataGridColumn;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  rowData: Record<string, unknown>;
}

function CellEditor({
  column,
  value,
  onChange,
  disabled,
  rowData,
}: CellEditorProps) {
  const baseInputClass =
    "w-full rounded border border-border bg-muted px-2 py-1 text-sm text-foreground focus:border-emerald-500 focus:outline-none disabled:opacity-50";

  switch (column.type) {
    case "string":
      return (
        <input
          type="text"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={column.placeholder}
          disabled={disabled}
          className={baseInputClass}
        />
      );

    case "number":
      return (
        <input
          type="number"
          value={(value as number) ?? ""}
          onChange={(e) => {
            const num = parseFloat(e.target.value);
            onChange(isNaN(num) ? "" : num);
          }}
          min={column.min}
          max={column.max}
          placeholder={column.placeholder}
          disabled={disabled}
          className={baseInputClass}
        />
      );

    case "dropdown":
      const options: DropdownOption[] = column.options || [];
      return (
        <select
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={baseInputClass}
        >
          <option value="">Select...</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case "boolean":
      return (
        <div className="flex justify-center">
          <input
            type="checkbox"
            checked={(value as boolean) ?? false}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            className="h-4 w-4 rounded border-border bg-secondary text-emerald-500 focus:ring-emerald-500"
          />
        </div>
      );

    case "date":
      return (
        <input
          type="date"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={baseInputClass}
        />
      );

    default:
      return (
        <input
          type="text"
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={baseInputClass}
        />
      );
  }
}
